/* eslint-disable react/prop-types */
/**
 * OnScreenKeyboard — RestroPulse POS (Sprint 1)
 *
 * Two layouts:
 *   NumericKeypad     — digits, decimal, backspace, clear. Used for money
 *                       entry (payment amounts, line quantities).
 *   AlphaKeyboard     — compact QWERTY row + digits, space, backspace,
 *                       enter, shift. Used for cooking notes and text.
 *
 * SUPPRESSING THE OS KEYBOARD
 * ---------------------------
 * The input element in each screen carries:
 *   inputMode="none"      → primary mechanism, respected by iOS Safari,
 *                            Chrome/Edge on Windows tablets and Android.
 *   readOnly              → NOT USED. It would block physical keyboards.
 *   onKeyDown={handleKey} → keeps physical keyboards working, which is
 *                            faster than tapping for real cashiers.
 *
 * The keyboard component itself calls back to `onKey({key})` with the
 * SAME shape as a KeyboardEvent — so the parent's onKeyDown handler is
 * the single source of truth for character insertion / backspace /
 * enter, regardless of whether the character came from a tap or a
 * physical keypress.
 *
 * FALLBACK CAVEAT: If on some target browser inputMode="none" fails to
 * suppress the OS keyboard, the correct fix at that time is to set
 * readOnly on the visible input and route physical keydown to a hidden
 * focused element. Not done yet — we haven't seen it fail in practice.
 */
import React from "react";
import { Delete, CornerDownLeft } from "lucide-react";

// ─── Numeric keypad (money & quantity) ──────────────────────────────
export function NumericKeypad({ onKey, quickTender, className = "" }) {
  // Item 1 (Sprint 3 corrections) · primaryCell is DEDICATED (does not
  // extend `cell`). Extending `cell` re-adds `bg-white text-[color:var(--pos-color-primary-text,#1A2421)]`
  // which then fights the primary overrides — Tailwind can't resolve
  // the conflict deterministically and the outcome is white text on a
  // white fill in some browsers. Kept separate here.
  const cell = "h-14 min-w-[3.5rem] rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white "
             + "text-[22px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums "
             + "active:bg-[#F0F0EE] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] transition select-none";
  const primaryCell = "h-14 min-w-[3.5rem] rounded border border-transparent "
                    + "text-[22px] font-medium tabular-nums select-none "
                    + "bg-[color:var(--color-primary)] text-white "
                    + "hover:bg-[color:var(--color-primary-hover)] transition";
  const wide = cell + " col-span-1";
  const send = (k) => () => onKey({ key: k });

  return (
    <div className={`grid grid-cols-4 gap-2 ${className}`} data-testid="pos-numeric-keypad">
      {["7","8","9"].map((d) => (
        <button key={d} type="button" onClick={send(d)} className={cell} data-testid={`pos-num-${d}`}>{d}</button>
      ))}
      <button type="button" onClick={send("Backspace")}
        className={cell + " text-[#7A2E1F] flex items-center justify-center"}
        data-testid="pos-num-backspace" aria-label="Backspace">
        <Delete className="w-5 h-5" />
      </button>

      {["4","5","6"].map((d) => (
        <button key={d} type="button" onClick={send(d)} className={cell} data-testid={`pos-num-${d}`}>{d}</button>
      ))}
      <button type="button" onClick={send("Escape")} className={cell + " text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]"}
        data-testid="pos-num-clear">Clear</button>

      {["1","2","3"].map((d) => (
        <button key={d} type="button" onClick={send(d)} className={cell} data-testid={`pos-num-${d}`}>{d}</button>
      ))}
      {quickTender ? (
        <button type="button" onClick={() => quickTender("exact")}
          className={primaryCell + " text-[11px] font-semibold"}
          data-testid="pos-num-exact">Exact</button>
      ) : (
        <button type="button" onClick={send("Enter")}
          className={primaryCell + " flex items-center justify-center"}
          data-testid="pos-num-enter" aria-label="Enter">
          <CornerDownLeft className="w-5 h-5" />
        </button>
      )}

      <button type="button" onClick={send("0")} className={wide + " col-span-2"} data-testid="pos-num-0">0</button>
      <button type="button" onClick={send(".")} className={cell} data-testid="pos-num-dot">.</button>
      {quickTender ? (
        <button type="button" onClick={() => quickTender("500")}
          className={primaryCell + " text-[11px] font-semibold"}
          data-testid="pos-num-500">₹500</button>
      ) : (
        <button type="button" onClick={send("Escape")}
          className={cell + " text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]"} data-testid="pos-num-cancel">Cancel</button>
      )}

      {quickTender && (
        <>
          <button type="button" onClick={() => quickTender("1000")}
            className={primaryCell + " col-span-2 text-[13px] font-semibold"}
            data-testid="pos-num-1000">₹1,000</button>
          <button type="button" onClick={() => quickTender("2000")}
            className={primaryCell + " col-span-2 text-[13px] font-semibold"}
            data-testid="pos-num-2000">₹2,000</button>
        </>
      )}
    </div>
  );
}

// ─── Compact alphanumeric keyboard (cooking notes, customer name) ───
const ROWS = [
  ["q","w","e","r","t","y","u","i","o","p"],
  ["a","s","d","f","g","h","j","k","l"],
  ["z","x","c","v","b","n","m",","," "],
];

export function AlphaKeyboard({ onKey, className = "" }) {
  const [shift, setShift] = React.useState(false);
  const send = (k) => () => {
    onKey({ key: shift ? k.toUpperCase() : k });
    if (shift) setShift(false);
  };
  const cell = "h-11 min-w-[2.4rem] rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white "
             + "text-[15px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] "
             + "active:bg-[#F0F0EE] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] transition select-none";
  return (
    <div className={`space-y-1.5 ${className}`} data-testid="pos-alpha-keyboard">
      {ROWS.map((row, i) => (
        <div key={i} className="flex justify-center gap-1.5">
          {i === 2 && (
            <button type="button" onClick={() => setShift((s) => !s)}
              className={cell + ` px-3 text-[11px] ${shift ? 'bg-[color:var(--color-primary)]/10 border-[color:var(--color-primary)]' : ''}`}
              data-testid="pos-alpha-shift">Shift</button>
          )}
          {row.map((ch) => (
            <button key={ch} type="button" onClick={send(ch)} className={cell + " flex-1 max-w-[4.5rem]"}
              data-testid={`pos-alpha-${ch === ' ' ? 'space' : ch}`}>
              {ch === " " ? "space" : (shift ? ch.toUpperCase() : ch)}
            </button>
          ))}
          {i === 2 && (
            <button type="button" onClick={() => onKey({ key: "Backspace" })}
              className={cell + " px-3 text-[#7A2E1F] flex items-center justify-center"}
              data-testid="pos-alpha-backspace" aria-label="Backspace">
              <Delete className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
      <div className="flex justify-center gap-1.5 pt-0.5">
        <button type="button" onClick={() => onKey({ key: "Enter" })}
          className="h-11 px-6 rounded border border-transparent bg-[color:var(--color-primary)] text-white text-[15px] font-semibold hover:bg-[color:var(--color-primary-hover)] transition select-none"
          data-testid="pos-alpha-enter">Done</button>
      </div>
    </div>
  );
}

/**
 * Shared helper: apply a `KeyboardEvent`-shaped payload to a controlled
 * value string. Used by both onScreen buttons and physical onKeyDown
 * handlers so both paths behave identically.
 */
export function applyKeyToValue(value, key) {
  if (key === "Backspace") return value.slice(0, -1);
  if (key === "Escape")    return "";
  if (key === "Enter")     return value;                 // caller decides
  if (key.length === 1)    return value + key;
  return value;
}
